import api from '../utils/api';
import * as T from '../types';
import {
    CreateSedeRequest, CreateSedeResponse,
    Sede, SedeDto, SettingsDto, User, UserDto, UserRole,
    PreventivoDto, SendWhatsappRequest, ApiResponse, ActivityType, MovimentoContabile, FinancialStats,
    PendingApprovalItem
} from "../types";
import {File} from "lucide-react";

// --- Servizi CRUD standard ---

export const UserService = {
    getAll: async (): Promise<T.ApiResponse<T.User[]>> => {
        const response = await api.get<T.ApiResponse<T.User[]>>('/users');
        return response.data;
    },
    checkToken: async (): Promise<T.ApiResponse<T.User>> => {
        const response = await api.get<T.ApiResponse<T.User>>('/users/check');
        return response.data;
    },
    getInviteLink: async (id: number): Promise<T.ApiResponse<string>> => {
        const response = await api.get<T.ApiResponse<string>>(`/users/${id}/invite-link`);
        return response.data;
    },
    getById: async (id: number): Promise<T.ApiResponse<T.User>> => {
        const response = await api.get<T.ApiResponse<T.User>>(`/users/${id}`);
        return response.data;
    },
    getAgenda: async (id: number, from?: string, to?: string): Promise<any> => {
        const params: any = {};
        if (from) params.from = from;
        if (to) params.to = to;
        const response = await api.get(`/users/${id}/agenda`, { params });
        return response.data?.data ?? response.data;
    },
    changePassword: async (data: { oldPassword: string, newPassword: string, confirmPassword?: string }) => {
        // Il BE si aspetta { currentPassword, newPassword, confirmPassword }: mappiamo qui
        // per non propagare il rename nei call site. confirmPassword, se assente, replica
        // newPassword (la conferma è già fatta in UI).
        const payload = {
            currentPassword: data.oldPassword,
            newPassword: data.newPassword,
            confirmPassword: data.confirmPassword ?? data.newPassword,
        };
        const response = await api.post('/users/change-password', payload);
        return response.data;
    },
    create: async (data: T.UserDto): Promise<T.ApiResponse<T.User>> => {
        const response = await api.post<any>('/users', data);
        return response.data;
    },
    update: async (id: number, data: T.UserDto): Promise<T.ApiResponse<T.User>> => {
        const response = await api.put<T.ApiResponse<T.User>>(`/users/${id}`, data);
        return response.data;
    },
    delete: async (id: number): Promise<T.ApiResponse<boolean>> => {
        const response = await api.delete<T.ApiResponse<boolean>>(`/users/${id}`);
        return response.data;
    },
    recoverPassword: async (email: string): Promise<T.ApiResponse<boolean>> => {
        const response = await api.get<T.ApiResponse<boolean>>(`/users/recoverPassword/${email}`);
        return response.data;
    },
    checkRecoverToken: async (code: string): Promise<T.ApiResponse<boolean>> => {
        const response = await api.get<T.ApiResponse<boolean>>(`/users/checkRecoverPassword/${code}`);
        return response.data;
    },
    saveNewPassword: async (data: { code: string, newPassword: string, confirmPassword: string }): Promise<T.ApiResponse<boolean>> => {
        const response = await api.post<T.ApiResponse<boolean>>('/users/saveNewPassword', data);
        return response.data;
    },
    // Consenso all'invio di notifiche WhatsApp: senza questo flag il BE non usa Twilio.
    updateWhatsappOptIn: async (optIn: boolean): Promise<T.ApiResponse<{ whatsappOptIn: boolean }>> => {
        const response = await api.put<T.ApiResponse<{ whatsappOptIn: boolean }>>(
            '/users/me/whatsapp-opt-in', { optIn });
        return response.data;
    },

    // Avvisi da supervisore per area del proprio profilo (titolare/responsabile).
    getNotificheArea: async (): Promise<T.ApiResponse<{ notificheFiduciari: boolean; notificheInvestigativi: boolean }>> =>
        (await api.get('/users/me/notifiche')).data,
    updateNotificheArea: async (v: { notificheFiduciari: boolean; notificheInvestigativi: boolean }): Promise<T.ApiResponse<{ notificheFiduciari: boolean; notificheInvestigativi: boolean }>> =>
        (await api.put('/users/me/notifiche', v)).data,

    // Aggiorna il numero WhatsApp dell'utente.
    updatePhone: async (phone: string): Promise<T.ApiResponse<{ phone: string }>> => {
        const response = await api.put<T.ApiResponse<{ phone: string }>>(
            '/users/me/phone', { phone });
        return response.data;
    },
};

export const CompanyService = {
    getAll: async () => (await api.get('/companys')).data,
    getById: async (id: number) => (await api.get(`/companys/${id}`)).data,
    create: async (data: any) => (await api.post('/companys', data)).data,
    update: async (id: number, data: any) => (await api.put(`/companys/${id}`, data)).data,
    delete: async (id: number) => (await api.delete(`/companys/${id}`)).data,
};

export const MandatoService = {
    getAll: async () => (await api.get('/mandatos')).data,
    getById: async (id: number) => (await api.get(`/mandatos/${id}`)).data,
    create: async (data: any) => (await api.post('/mandatos', data)).data,
    update: async (id: number, data: any) => (await api.put(`/mandatos/${id}`, data)).data,
    delete: async (id: number) => (await api.delete(`/mandatos/${id}`)).data,
};

export const ServizioFiduciarioService = {
    getAll: (params?: { sedeId?: number }) => api.get('/servizi', { params }),
    getById: (id: number) => api.get(`/servizi/${id}`),
    create: (data: any) => api.post('/servizi', data),
    update: (id: number, data: any) => api.put(`/servizi/${id}`, data),
    // cascadePreventivi: true elimina anche preventivi/proforma collegati,
    // false li scollega lasciandoli standalone.
    delete: (id: number, cascadePreventivi: boolean = false) =>
        api.delete(`/servizi/${id}?cascadePreventivi=${cascadePreventivi}`),
    // Report e spese in attesa sui turni: se presenti il backend rifiuta l'eliminazione.
    preflightEliminazione: async (id: number): Promise<T.PreflightEliminazioneServizio> =>
        (await api.get<T.PreflightEliminazioneServizio>(`/servizi/${id}/preflight-eliminazione`)).data,
    getMyTurni: () => api.get('/servizi/turni/me'),
    getTurniOggi: () => api.get('/servizi/turni/oggi'),
    startTurno: (turnoId: number, lat: number, lon: number) => api.post(`/servizi/turni/${turnoId}/start`, { lat, lon }),
    stopTurno: (turnoId: number, lat: number, lon: number) => api.post(`/servizi/turni/${turnoId}/stop`, { lat, lon }),

    // Foto al punto vendita a start/stop (multipart, foto opzionale).
    startTurnoWithPhoto: (turnoId: number, lat: number, lon: number, foto?: File) => {
        const fd = new FormData();
        fd.append('lat', String(lat));
        fd.append('lon', String(lon));
        if (foto) fd.append('foto', foto);
        return api.post(`/servizi/turni/${turnoId}/start-with-photo`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    stopTurnoWithPhoto: (turnoId: number, lat: number, lon: number, foto?: File) => {
        const fd = new FormData();
        fd.append('lat', String(lat));
        fd.append('lon', String(lon));
        if (foto) fd.append('foto', foto);
        return api.post(`/servizi/turni/${turnoId}/stop-with-photo`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },

    // Note operative durante il turno.
    addNotaTurno: (turnoId: number, text: string, lat?: number, lon?: number) =>
        api.post(`/servizi/turni/${turnoId}/note`, { text, lat, lon }),
    getNoteTurno: (turnoId: number) =>
        api.get(`/servizi/turni/${turnoId}/note`),

    // Override delle ore pagate al collaboratore; null torna al calcolo GPS.
    setOreCollaboratore: (turnoId: number, ore: number | null) =>
        api.patch(`/servizi/turni/${turnoId}/ore-collaboratore`, { ore }),

    // Approva o disapprova esplicitamente le ore pagate.
    approvaOre: (turnoId: number, approva: boolean) =>
        api.patch(`/servizi/turni/${turnoId}/approva-ore`, { approva }),

    // Il collaboratore accetta o rifiuta il turno.
    accettaTurno: (turnoId: number) =>
        api.post(`/servizi/turni/${turnoId}/accetta`),
    rifiutaTurno: (turnoId: number, motivo: string) =>
        api.post(`/servizi/turni/${turnoId}/rifiuta`, { motivo }),

    // Scadenza opzionale per l'accettazione del turno (null per rimuoverla).
    // Allo scadere il BE porta il turno a RIFIUTATO.
    setScadenzaAccettazione: (turnoId: number, when: string | null) =>
        api.patch(`/servizi/turni/${turnoId}/scadenza-accettazione`,
            { dataScadenzaAccettazione: when }),
    // Lista compatta per badge Sidebar / banner Dashboard collab.
    getMieiTurniDaAccettare: () =>
        api.get(`/servizi/turni/mie/da-accettare`),

    // Revoca del turno da parte del titolare.
    revocaTurno: (turnoId: number) =>
        api.put(`/servizi/turni/${turnoId}/revoca`),

    // Modifica solo l'ora di fine ("HH:mm"), anche a turno avviato; ricalcola il notturno.
    // 409 se revocato o se la riduzione scende sotto il lavoro effettivo.
    // Nota: cambia il ricavo cliente (ore x tariffa).
    updateOraFineTurno: (turnoId: number, oraFine: string) =>
        api.patch(`/servizi/turni/${turnoId}/ora-fine`, { oraFine }),

    // Sposta giorno, inizio e fine ("HH:mm") di un turno non ancora avviato. 409 con `conflicts`
    // se il collaboratore è già impegnato. Cambia il ricavo cliente (ore pianificate).
    updateOrariTurno: (turnoId: number, body: { dataTurno: string; oraInizio: string; oraFine: string; forceOverride?: boolean }) =>
        api.patch(`/servizi/turni/${turnoId}/orari`, body),

    // Aggiorna la finestra di tolleranza del turno; ogni lato è "HH:mm" o null (nessun limite).
    updateRangeTurno: (turnoId: number, range: { rangeInizioDa: string | null; rangeInizioA: string | null; rangeFineDa: string | null; rangeFineA: string | null }) =>
        api.patch(`/servizi/turni/${turnoId}/range`, range),

    // Disattiva/riattiva il controllo geofence per il singolo turno.
    setGeofenceDisabilitato: (turnoId: number, disabilitato: boolean) =>
        api.patch(`/servizi/turni/${turnoId}/geofence-disabilitato`, { disabilitato }),

    // Patch dei dati aggiuntivi dei movimenti contabili.
    patchDatiAggiuntivi: (servizioId: number, datiAggiuntiviJson: string) =>
        api.patch(`/servizi/${servizioId}/dati-aggiuntivi`, { datiAggiuntiviJson }),

    // Gestione Tariffe
    addTariffa: (servizioId: number, data: any) => api.post(`/servizi/${servizioId}/tariffe`, data),
    // Abilita tutti quelli con un costo orario sicurezza > 0, con quella tariffa.
    addTariffeSicurezzaTutti: (servizioId: number) => api.post(`/servizi/${servizioId}/tariffe/tutti-sicurezza`),
    removeTariffa: (servizioId: number, tariffaId: number) => api.delete(`/servizi/${servizioId}/tariffe/${tariffaId}`),
    updateTariffa: (servizioId: number, tariffaId: number, tariffaOraria: number) =>
        api.patch(`/servizi/${servizioId}/tariffe/${tariffaId}`, { tariffaOraria }),

    // Gestione Turni
    saveTurniBulk: (servizioId: number, turni: any[]) => api.post(`/servizi/${servizioId}/turni/bulk`, turni),
    // Altri collaboratori già presenti sul servizio negli orari indicati (avviso prima di salvare).
    compresenze: async (servizioId: number, body: { collaboratoreId: number | null; escludiTurnoId: number | null; slots: T.SlotTurno[] }): Promise<T.Compresenza[]> => {
        const res = await api.post(`/servizi/${servizioId}/turni/compresenze`, body);
        return Array.isArray(res.data) ? res.data : [];
    },
    deleteTurno: (servizioId: number, turnoId: number) => api.delete(`/servizi/${servizioId}/turni/${turnoId}`),

    // Riepilogo mensile delle ore fiduciarie di un collaboratore (turni chiusi):
    // { oreTotali, importoTotale, righe[{ data, servizioNome, ore, tariffa, importo }] }.
    getOreFiduciarie: (userId: number, anno: number, mese: number) =>
        api.get('/servizi/ore-fiduciarie', { params: { userId, anno, mese } }),
};

export const PraticaService = {
    getAll: async (params: {
        anno?: number;
        tab?: string;
        search?: string;
        sedeId?: number;
        page?: number;
        size?: number;
        modelId?: string;
        stato?: string;
    }) => {
        const response = await api.get('/praticas', { params });
        return response.data;
    },
    getById: async (id: number) => (await api.get(`/praticas/${id}`)).data,
    // Riepilogo economico per la modale di registrazione fattura a mandato concluso:
    // imponibile + IVA, incassato e residuo, con la stessa logica del tab Contabilità,
    // più i conteggi degli incarichi ancora aperti.
    getRiepilogoChiusura: async (id: number): Promise<{
        tipoCompenso: string;
        baseImponibile: number;
        baseIvato: number;
        totaleIncassato: number;
        saldoResiduo: number;
        // Incarichi ancora aperti, per l'avviso prima di concludere.
        incarichiDaAccettare?: number;
        incarichiAccettati?: number;
        incarichiInCorso?: number;
        incarichiAperti?: number;
    }> => {
        const res = await api.get(`/praticas/${id}/riepilogo-chiusura`);
        return res.data?.data ?? res.data;
    },
    // Mandati attivi di un cliente, per la modale "Collega a mandato".
    getByClient: async (clientId: number): Promise<ApiResponse<any[]>> => {
        const response = await api.get<ApiResponse<any[]>>(`/praticas/by-client/${clientId}`);
        return response.data;
    },
    // Numero totale di mandati per cliente (id cliente -> conteggio), per la lista clienti.
    countByClient: async (): Promise<Record<string, number>> => {
        const response = await api.get<ApiResponse<Record<string, number>>>('/praticas/count-by-client');
        return (response.data as any)?.data ?? {};
    },
    create: async (data: any) => (await api.post('/praticas', data)).data,
    update: async (id: number, data: any) => (await api.put(`/praticas/${id}`, data)).data,
    patchDatiAggiuntivi: async (id: number, fields: Record<string, any>) => (await api.patch(`/praticas/${id}/dati-aggiuntivi`, fields)).data,
    // Note libere del tab Contabilità (il debounce è nella view).
    patchNoteContabilita: async (id: number, noteContabilita: string) => (await api.patch(`/praticas/${id}/note-contabilita`, { noteContabilita })).data,
    // Modifica inline dell'etichetta del mandato (solo titolare/responsabile).
    patchRiferimento: async (id: number, riferimento: string) => (await api.patch(`/praticas/${id}/riferimento`, { riferimento })).data,
    // Importi che andrebbero persi eliminando il mandato: va chiamato prima
    // della conferma, perché la delete li azzera.
    preflightEliminazione: async (id: number) =>
        (await api.get<ApiResponse<T.PreflightEliminazione>>(`/praticas/${id}/preflight-eliminazione`)).data,
    // cascadePreventivi: true elimina anche preventivi/proforma collegati,
    // false li scollega lasciandoli standalone.
    delete: async (id: number, cascadePreventivi: boolean = false) =>
        (await api.delete(`/praticas/${id}?cascadePreventivi=${cascadePreventivi}`)).data,
    // Svuotamento selettivo dal File Manager: rimuove da S3 e DB le categorie scelte
    // (prove, foto km, documenti d'ufficio). Mandato, incarichi, report e contabilità restano.
    svuotaMedia: async (id: number, opts: { prove?: boolean; fotoKm?: boolean; documenti?: boolean }) =>
        (await api.delete(`/praticas/${id}/media?prove=${!!opts.prove}&fotoKm=${!!opts.fotoKm}&documenti=${!!opts.documenti}`)).data,
    // Svuota i dati del soggetto investigato di un mandato sospeso, concluso, archiviato o annullato (restano le iniziali).
    svuotaDatiSoggetto: async (id: number, opts: { eliminaRelazione: boolean; eliminaPdfSoggetto: boolean }) =>
        (await api.post(`/praticas/${id}/svuota-soggetto`, null, {
            params: { eliminaRelazione: opts.eliminaRelazione, eliminaPdfSoggetto: opts.eliminaPdfSoggetto },
        })).data,
    // Conteggi per categoria per il modale di svuotamento.
    mediaCounts: async (id: number): Promise<{ success: boolean; data: { prove: number; fotoKm: number; documenti: number } }> =>
        (await api.get(`/praticas/${id}/media-counts`)).data,
    // preview=true → per i video la copia MP4 convertita se esiste (anteprima player).
    getDownloadUrl: (fileId: number, preview: boolean = false) =>
        api.get(`/praticas/download-link/${fileId}`, preview ? { params: { preview: true } } : undefined).then(res => res.data),
    getHistory: async (id: number) => {
        try {
            const response = await api.get(`/praticas/${id}/history`);
            return response.data;
        } catch (error) {
            console.error("Errore nel recupero dello storico", error);
            throw error;
        }
    },
    cambiaStato: async (id: number, stato: string) => {
        const response = await api.put(`/praticas/${id}/stato`, { stato });
        return response.data;
    },
    registraFattura: async (id: number, data: { importoFatturato: number; note?: string }) => {
        const response = await api.post(`/praticas/${id}/fattura`, data);
        return response.data;
    },
    downloadRelazionePdf: async (id: number, soggetto?: string): Promise<Blob> => {
        // soggetto opzionale: PDF della relazione di quel soggetto; se assente, relazione unica.
        const q = soggetto ? `?soggetto=${encodeURIComponent(soggetto)}` : '';
        const response = await api.get(`/praticas/${id}/download-relazione${q}`, {
            responseType: 'blob',
            skipGlobalErrorToast: true, // l'errore lo gestisce il chiamante (fallback client / anteprima live)
        });
        return response.data;
    },
    updatePraticaMultipart: async (id: number, formData: FormData) => {
        const response = await api.put(`/praticas/${id}/multipart`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    },
    attivaPratica: async (id: number) => {
        const response = await api.put(`/praticas/${id}/attiva`);
        return response.data;
    },
    avviaFirmaOTP: async (praticaId: number, payload: any) => {
        const response = await api.post(`/praticas/${praticaId}/avvia-firma`, payload);
        return response.data;
    },
    // Ricostruisce il destinatario che verrebbe inviato a OTP senza creare il dossier,
    // per controllare i dati senza consumare firme.
    avviaFirmaOTPDryRun: async (praticaId: number, payload: any) => {
        const response = await api.post(`/praticas/${praticaId}/avvia-firma-dryrun`, payload);
        return response.data;
    },
    downloadMandatoFirmato: async (dossierId: number, filename = 'mandato_firmato.pdf'): Promise<void> => {
        const response = await api.get(`/otp/dossiers/${dossierId}/signed-pdf`, { responseType: 'blob' });
        const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 10000); // Safari annulla il download se si revoca subito
    },
    createPraticaMultipart: async (formData: FormData) => {
        const response = await api.post('/praticas', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });

        return response.data;
    },
    getRelazione: async (id: number, soggetto?: string) => {
        // Contenuto HTML della relazione salvata (per soggetto se indicato).
        const q = soggetto ? `?soggetto=${encodeURIComponent(soggetto)}` : '';
        const response = await api.get(`/praticas/${id}/relazione${q}`);
        return response.data;
    },

    saveRelazione: async (id: number, content: string, soggetto?: string) => {
        // Il BE legge `soggetto` dal body: se presente salva la relazione di quel soggetto.
        const body: { content: string; soggetto?: string } = { content };
        if (soggetto) body.soggetto = soggetto;
        const response = await api.post(`/praticas/${id}/relazione`, body);
        return response.data;
    },

    resetRelazione: async (id: number, soggetto?: string) => {
        // Elimina la bozza e ripristina il template vuoto (solo quel soggetto, se indicato).
        const q = soggetto ? `?soggetto=${encodeURIComponent(soggetto)}` : '';
        const response = await api.delete(`/praticas/${id}/relazione${q}`);
        return response.data;
    }
};

export const FigureProfessionaliService = {
    getAll: async () => (await api.get('/figure-professionali')).data,
    create: async (data: any) => (await api.post('/figure-professionali', data)).data,
    update: async (id: number, data: any) => (await api.put(`/figure-professionali/${id}`, data)).data,
    delete: async (id: number) => (await api.delete(`/figure-professionali/${id}`)).data,
};

export const ClientService = {
    // Riepilogo attività per id cliente (mandati per tipo, servizi, mesi di lavoro) per i filtri della lista.
    riepilogoAttivita: async (): Promise<Record<string, T.ClienteAttivita>> => {
        const response = await api.get<ApiResponse<Record<string, T.ClienteAttivita>>>('/clients/riepilogo-attivita');
        return (response.data as any)?.data ?? {};
    },
    getAll: async (): Promise<ApiResponse<T.Client[]>> => {
        const response = await api.get<ApiResponse<T.Client[]>>('/clients');
        return response.data;
    },
    getById: async (id: number | string): Promise<ApiResponse<T.Client>> => {
        const response = await api.get<ApiResponse<T.Client>>(`/clients/${id}`);
        return response.data;
    },
    create: async (data: any): Promise<ApiResponse<T.Client>> => {
        const response = await api.post<ApiResponse<T.Client>>('/clients', data);
        return response.data;
    },

    createMultipart: async (formData: FormData): Promise<ApiResponse<T.Client>> => {
        const response = await api.post<ApiResponse<T.Client>>('/clients/multipart', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    },

    updateMultipart: async (id: number | string, formData: FormData): Promise<ApiResponse<T.Client>> => {
        const response = await api.put<ApiResponse<T.Client>>(`/clients/multipart/${id}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    },

    deleteFile: async (fileId: number | string): Promise<ApiResponse<boolean>> => {
        const response = await api.delete<ApiResponse<boolean>>(`/clients/files/${fileId}`);
        return response.data;
    },

    update: async (id: number | string, data: any): Promise<ApiResponse<T.Client>> => {
        const response = await api.put<ApiResponse<T.Client>>(`/clients/${id}`, data);
        return response.data;
    },
    delete: async (id: number | string): Promise<ApiResponse<boolean>> => {
        const response = await api.delete<ApiResponse<boolean>>(`/clients/${id}`);
        return response.data;
    },
};

export const FilesService = {
    getAll: async () => (await api.get('/filess')).data,
    getById: async (id: number) => (await api.get(`/filess/${id}`)).data,
    create: async (data: any) => (await api.post('/filess', data)).data,
    update: async (id: number, data: any) => (await api.put(`/filess/${id}`, data)).data,
    delete: async (id: number) => (await api.delete(`/filess/${id}`)).data,
    updateFileDate: async (fileId: number, dataAcquisizione: string): Promise<any> => {
        return (await api.patch(`/filess/${fileId}/date`, { dataAcquisizione })).data;
    },
    uploadToPratica: async (praticaId: number, file: File, applyWatermark: boolean = true, categoria?: string) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('praticaId', praticaId.toString());
        formData.append('applyWatermark', applyWatermark.toString());
        if (categoria) formData.append('categoria', categoria);
        const response = await api.post('/filess/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data?.data ?? response.data;
    },
    uploadGenerale: (file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/filess/upload/generale', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },

    // preview=true → per i video la copia MP4 convertita se esiste (anteprima player).
    // download=true → link in attachment: il browser scarica invece di aprire una nuova pagina.
    getDownloadUrl: (id: number, preview: boolean = false, download: boolean = false) =>
        api.get(`/filess/${id}/download-url`, (preview || download) ? { params: { ...(preview ? { preview: true } : {}), ...(download ? { download: true } : {}) } } : undefined),
};

export const FolderService = {
    getAll: () => api.get('/folders'),
    create: (name: string) => api.post('/folders', { name }),
    delete: (id: number) => api.delete(`/folders/${id}`),
    uploadToFolder: (folderId: number, file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post(`/filess/upload/folder/${folderId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' }});
    }
};

// Cartella "Doc. Ufficio": documenti dei collaboratori in PDF e registro dei download.
export const DocUfficioService = {
    collaboratori: async (): Promise<{ id: number; tipo: string; titolo: string; versione: number; inVigoreDal?: string }[]> => {
        const response = await api.get('/doc-ufficio/collaboratori');
        return response.data?.data ?? [];
    },
    pdfCollaboratori: async (id: number): Promise<{ url: string; titolo: string; versione: number }> => {
        const response = await api.get(`/doc-ufficio/collaboratori/${id}/pdf`);
        return response.data?.data;
    },
    moduloScaricato: async (tipo: string): Promise<void> => {
        await api.post(`/doc-ufficio/moduli/${tipo}/scaricato`);
    },
};

export const PersonalDriveService = {
    list: async (parentId?: number | null) => {
        const params: any = {};
        if (parentId != null) params.parentId = parentId;
        const res = await api.get('/personal-drive', { params });
        return res.data as Array<{
            id: number;
            name: string;
            folder: boolean;
            parentId: number | null;
            mimeType?: string;
            sizeBytes?: number;
            extension?: string;
            url?: string;
            createdAt: string;
        }>;
    },
    createFolder: async (name: string, parentId: number | null = null) => {
        const res = await api.post('/personal-drive/folder', { name, parentId });
        return res.data;
    },
    // Diretto sul bucket come le prove (a parti oltre 25 MB): nessun limite di dimensione
    // e il file non passa da Cloudflare. Il backend registra poi la voce nel drive.
    upload: async (file: File, parentId: number | null = null, onProgress?: (bytes: number) => void) => {
        const base = '/personal-drive/upload';
        const s3Key = await IncaricoService.uploadToS3Multipart(file, onProgress, base);
        const res = await api.post(`${base}/register`, {
            s3Key, name: file.name, contentType: file.type || 'application/octet-stream', parentId,
        });
        return res.data;
    },
    delete: async (id: number) => (await api.delete(`/personal-drive/${id}`)).data,
    getDownloadUrl: async (id: number, download: boolean = false): Promise<string> => {
        const res = await api.get(`/personal-drive/${id}/download`, download ? { params: { download: true } } : undefined);
        return res.data?.data || res.data;
    },
};

export const InviteService = {
    validateToken: async (token: string) => {
        const response = await api.get(`/users/checkInvite/${token}`);
        // data=false: invito scaduto, già usato o sconosciuto (la risposta resta 200).
        return {
            valid: response.data?.success === true && response.data?.data === true,
            data: response.data.data
        };
    },
    // Nome, cognome ed email inseriti dal titolare, per precompilare la registrazione.
    getInviteData: async (token: string): Promise<{ nome?: string; cognome?: string; email?: string }> => {
        const response = await api.get(`/users/checkInvite/${token}/dati`);
        return response.data?.data || {};
    },
    // `acceptances` riporta id/tipo/versione dei documenti legali accettati;
    // il BE li registra con IP e user agent.
    completeRegistration: async (
        token: string,
        password: string,
        nome: string,
        cognome: string,
        acceptances: Array<{ documentId: number; documentType: string; version: number }>
    ) => {
        const payload = {
            code: token,
            password: password,
            name: nome,
            surname: cognome,
            email: "unused@frontend.com",
            acceptances
        };
        const response = await api.post('/users/complete-invite', payload);
        return response.data;
    }
};

// Documenti legali (endpoint pubblici, senza JWT). Tipo definito qui
// perché non presente in types.ts.
export type LegalDocumentDTO = {
    id: number;
    type: 'PRIVACY_POLICY' | 'TERMS_OF_SERVICE' | 'DPA_INSTRUCTIONS' | 'WORKER_MONITORING_NOTICE' | 'CODICE_ETICO';
    version: number;
    title: string;
    contentHtml: string;
    changelogNote: string | null;
    mandatory: boolean;
};

// Vista admin completa di una versione di documento legale.
export type LegalDocumentAdminDTO = {
    id: number;
    type: LegalDocumentDTO['type'];
    version: number;
    title: string;
    templateHtml: string | null;
    contentHtml: string | null;
    changelogNote: string | null;
    contentHash: string | null;
    draft: boolean;
    inForce: boolean;
    publishedAt: string | null;
    effectiveFrom: string | null;
    supersededByVersion: number | null;
    signaturesCount: number;
};

export type LegalDocumentUpsert = {
    type: LegalDocumentDTO['type'];
    title: string;
    templateHtml: string;
    changelogNote?: string | null;
};

export const LegalDocumentService = {
    // Documenti da firmare per il completamento di un invito.
    getForInvite: async (token: string): Promise<LegalDocumentDTO[]> => {
        const response = await api.get(`/public/legal/documents/for-invite/${token}`);
        return (response.data?.data ?? []) as LegalDocumentDTO[];
    },
    // Documenti in vigore per un dato ruolo.
    getCurrentForRole: async (role: string): Promise<LegalDocumentDTO[]> => {
        const response = await api.get(`/public/legal/documents/current`, { params: { role } });
        return (response.data?.data ?? []) as LegalDocumentDTO[];
    },

    // --- Admin ---
    adminList: async (): Promise<LegalDocumentAdminDTO[]> => {
        const response = await api.get('/admin/legal/documents');
        return (response.data?.data ?? []) as LegalDocumentAdminDTO[];
    },
    // Placeholder disponibili con il valore corrente preso da Impostazioni.
    adminPlaceholders: async (): Promise<Record<string, string>> => {
        const response = await api.get('/admin/legal/documents/placeholders');
        return (response.data?.data ?? {}) as Record<string, string>;
    },
    adminCreate: async (payload: LegalDocumentUpsert): Promise<LegalDocumentAdminDTO> => {
        const response = await api.post('/admin/legal/documents', payload);
        return response.data?.data as LegalDocumentAdminDTO;
    },
    adminUpdate: async (id: number, payload: LegalDocumentUpsert): Promise<LegalDocumentAdminDTO> => {
        const response = await api.put(`/admin/legal/documents/${id}`, payload);
        return response.data?.data as LegalDocumentAdminDTO;
    },
    adminDelete: async (id: number): Promise<void> => {
        await api.delete(`/admin/legal/documents/${id}`);
    },
    // Anteprima con placeholder risolti, senza pubblicare.
    adminPreview: async (payload: LegalDocumentUpsert): Promise<string> => {
        const response = await api.post('/admin/legal/documents/preview', payload);
        return (response.data?.data?.contentHtml ?? '') as string;
    },
    adminPublish: async (id: number): Promise<LegalDocumentAdminDTO> => {
        const response = await api.post(`/admin/legal/documents/${id}/publish`, {});
        return response.data?.data as LegalDocumentAdminDTO;
    },
    // Ripubblica risolvendo i placeholder con i dati attuali di Impostazioni.
    adminRepublish: async (id: number): Promise<LegalDocumentAdminDTO> => {
        const response = await api.post(`/admin/legal/documents/${id}/republish`, {});
        return response.data?.data as LegalDocumentAdminDTO;
    },
};

// Stato consensi dell'utente autenticato e ri-accettazione.
export type ConsentStatus = {
    pending: LegalDocumentDTO[];
    accepted: Array<{ documentId: number; documentType: string; documentVersion: number; acceptedAt: string }>;
};

// Etichette leggibili per tipo di documento legale.
export const LEGAL_DOC_TYPE_LABELS: Record<string, string> = {
    PRIVACY_POLICY: 'Informativa privacy',
    TERMS_OF_SERVICE: 'Termini e condizioni',
    DPA_INSTRUCTIONS: 'Istruzioni al designato al trattamento',
    WORKER_MONITORING_NOTICE: 'Informativa controllo a distanza (GPS/foto)',
    CODICE_ETICO: 'Codice etico',
};
export const legalDocTypeLabel = (t: string): string => LEGAL_DOC_TYPE_LABELS[t] || t;

export const ConsentService = {
    getStatus: async (): Promise<ConsentStatus> => {
        const response = await api.get('/me/consents/status');
        return (response.data?.data ?? { pending: [], accepted: [] }) as ConsentStatus;
    },
    // Stato consensi di un utente specifico (solo titolare/admin).
    getUserConsents: async (userId: number): Promise<ConsentStatus> => {
        const response = await api.get(`/admin/consents/user/${userId}`);
        return (response.data?.data ?? { pending: [], accepted: [] }) as ConsentStatus;
    },
    // Testo di un documento già accettato dall'utente.
    getConsentedDocument: async (documentId: number): Promise<any | null> => {
        const response = await api.get(`/me/consents/document/${documentId}`);
        return response.data?.data ?? null;
    },
    accept: async (
        acceptances: Array<{ documentId: number; documentType: string; version: number }>
    ): Promise<ConsentStatus> => {
        const response = await api.post('/me/consents/accept', { acceptances });
        return (response.data?.data ?? { pending: [], accepted: [] }) as ConsentStatus;
    },
};

export const SedeService = {
    getAll: async (): Promise<T.ApiResponse<T.Sede[]>> => {
        const response = await api.get<T.ApiResponse<T.Sede[]>>('/sedes');
        return response.data;
    },
    getById: async (id: number | string): Promise<T.ApiResponse<T.Sede>> => {
        const response = await api.get<T.ApiResponse<T.Sede>>(`/sedes/${id}`);
        return response.data;
    },
    create: async (data: T.CreateSedeRequest): Promise<T.CreateSedeResponse> => {
        const response = await api.post<T.ApiResponse<T.SedeDto>>('/sedes', data);
        const createdSedeDto = response.data.data;
        return {
            sede: createdSedeDto as unknown as T.Sede,
            inviteLink: createdSedeDto.inviteLink
        };
    },
    update: async (id: number | string, data: any): Promise<T.ApiResponse<T.Sede>> => {
        const response = await api.put<T.ApiResponse<T.Sede>>(`/sedes/${id}`, data);
        return response.data;
    },
    delete: async (id: number): Promise<{ success: boolean; message?: string }> => {
        try {
            const response = await api.delete<T.ApiResponse<boolean>>(`/sedes/${id}`);
            return { success: response.data.success, message: response.data.message };
        } catch (e: any) {
            return { success: false, message: e?.response?.data?.message || "Errore di rete durante l'eliminazione" };
        }
    }
};

export const getAuditLogs = (page: number, size: number, filters?: {
    action?: string;
    persona?: string;
    categoria?: string;
    rifTipo?: string;
    rifId?: number;
    from?: string;
    to?: string;
}) => {
    const params = new URLSearchParams({ page: String(page), size: String(size), sort: 'timestamp,desc' });
    Object.entries(filters || {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
    });
    return api.get(`/admin/audit?${params.toString()}`);
};

export const SettingsService = {
    getAll: async () => {
        const response = await api.get<T.ApiResponse<SettingsDto[]>>('/settingss');
        return response.data;
    },
    // Configurazione IA di default, per "Ripristina predefiniti".
    getAiDefaults: async () => {
        const response = await api.get<T.ApiResponse<Partial<SettingsDto>>>('/settingss/ai-defaults');
        return response.data;
    },
    update: async (id: number, data: SettingsDto) => {
        const response = await api.put<T.ApiResponse<SettingsDto>>(`/settingss/${id}`, data);
        return response.data;
    },
    create: async (data: SettingsDto) => {
        const response = await api.post<T.ApiResponse<SettingsDto>>('/settingss', data);
        return response.data;
    },
    uploadBrandingImage: async (file: File, type: 'logo' | 'header' | 'footer'): Promise<ApiResponse<SettingsDto>> => {
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post(`/settingss/upload-${type}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    },
    uploadMandatoImage: async (file: File, type: 'mandato-header' | 'mandato-footer'): Promise<ApiResponse<SettingsDto>> => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post(`/settingss/upload-${type}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    },
    uploadPrivacyPolicy: async (file: File): Promise<ApiResponse<SettingsDto>> => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post('/settingss/upload-privacy-policy', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    },
    // Timbro agenzia, stampato in basso a destra nell'ultima pagina della relazione.
    uploadTimbro: async (file: File): Promise<ApiResponse<SettingsDto>> => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post('/settingss/upload-timbro', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    },
    // Rimuove un'immagine di branding: il BE azzera la chiave e cancella il file.
    removeBrandingImage: async (type: 'logo' | 'header' | 'footer' | 'mandato-header' | 'mandato-footer' | 'timbro'): Promise<ApiResponse<SettingsDto>> => {
        const response = await api.delete(`/settingss/branding/${type}`);
        return response.data;
    },
};

// Storico difformità orarie. `tipo` opzionale: 'INCARICO' | 'TURNO' | 'TUTTI'.
export const AllertOrariDifformiService = {
    search: async (
        from: string,
        to: string,
        page: number = 0,
        size: number = 20,
        sedeId?: number,
        tipo?: T.AllertDifformitaTipo | 'TUTTI',
    ) => {
        const params: any = { from, to, page, size };
        if (typeof sedeId === 'number') params.sedeId = sedeId;
        if (tipo && tipo !== 'TUTTI') params.tipo = tipo;
        const response = await api.get<T.ApiResponse<T.SpringPage<T.AllertOrariDifformi>>>(
            '/allerti-orari-difformi',
            { params }
        );
        return response.data;
    },
    // Backfill una tantum sugli incarichi storici (solo admin, idempotente).
    backfill: async () => {
        const response = await api.post<T.ApiResponse<number>>('/allerti-orari-difformi/backfill');
        return response.data;
    },
};

// Storico allert geofence.
export const AllertGeofenceService = {
    search: async (
        from: string,
        to: string,
        page: number = 0,
        size: number = 20,
        sedeId?: number,
    ) => {
        const params: any = { from, to, page, size };
        if (typeof sedeId === 'number') params.sedeId = sedeId;
        const response = await api.get<T.ApiResponse<T.SpringPage<T.AllertGeofence>>>(
            '/allerti-geofence',
            { params }
        );
        return response.data;
    },
};

// Avvisi in sola lettura sulle foto arrivate dalla coda offline con EXIF assente
// o fuori tolleranza: la foto è già accettata, l'avviso è solo informativo.
export const AllertMediaService = {
    search: async (
        from: string,
        to: string,
        page: number = 0,
        size: number = 20,
        sedeId?: number,
    ) => {
        const params: any = { from, to, page, size };
        if (typeof sedeId === 'number') params.sedeId = sedeId;
        const response = await api.get<T.ApiResponse<T.SpringPage<T.AllertMedia>>>(
            '/allerti-media',
            { params }
        );
        return response.data;
    },
};

export const ResponsabileService = {
    getAll: async () => {
        const response = await api.get<T.ApiResponse<User[]>>('/users');
        const responsabili = (response.data.data || []).filter((u: User) =>
            u.role === UserRole.RESPONSABILE || (u as any).role === 'RESPONSABILE'
        );
        return { data: responsabili };
    },
    invite: async (data: { name: string, surname: string, email: string }) => {
        const payload: UserDto = { ...data, role: UserRole.RESPONSABILE } as UserDto;
        const response = await api.post<T.ApiResponse<string>>('/users', payload);
        return response.data;
    },
    delete: async (id: number) => {
        const response = await api.delete<T.ApiResponse<boolean>>(`/users/${id}`);
        return response.data;
    }
};

export const PreventiviService = {
    getAll: async (): Promise<ApiResponse<PreventivoDto[]>> => {
        const response = await api.get<ApiResponse<PreventivoDto[]>>('/preventivi');
        return response.data;
    },
    getById: async (id: string | number): Promise<ApiResponse<PreventivoDto>> => {
        const response = await api.get<ApiResponse<PreventivoDto>>(`/preventivi/${id}`);
        return response.data;
    },
    create: async (data: any): Promise<ApiResponse<PreventivoDto>> => {
        const response = await api.post<ApiResponse<PreventivoDto>>('/preventivi', data);
        return response.data;
    },
    update: async (id: string | number, data: any): Promise<ApiResponse<PreventivoDto>> => {
        const response = await api.put<ApiResponse<PreventivoDto>>(`/preventivi/${id}`, data);
        return response.data;
    },
    // Proforma da preventivo: record PROFORMA a sé (numero PF); il BE la riusa
    // (reused=true) se il contenuto del preventivo non è cambiato.
    proformaDaPreventivo: async (id: string | number, nascondiIva?: boolean): Promise<ApiResponse<PreventivoDto>> => {
        const response = await api.post<ApiResponse<PreventivoDto>>(`/preventivi/${id}/proforma`, { nascondiIva });
        return response.data;
    },
    updateStato: async (id: number | string, stato: string): Promise<ApiResponse<any>> => {
        const response = await api.put<ApiResponse<any>>(`/preventivi/${id}/stato`, { stato });
        return response.data;
    },
    sendWhatsapp: async (id: string | number, payload: SendWhatsappRequest): Promise<ApiResponse<any>> => {
        const response = await api.post<ApiResponse<any>>(`/preventivi/${id}/send-whatsapp`, payload);
        return response.data;
    },
    delete: async (id: number | string): Promise<ApiResponse<boolean>> => {
        const response = await api.delete<ApiResponse<boolean>>(`/preventivi/${id}`);
        return response.data;
    },
    // Preventivi collegati al mandato.
    getByPratica: async (praticaId: number | string): Promise<ApiResponse<PreventivoDto[]>> => {
        const response = await api.get<ApiResponse<PreventivoDto[]>>(`/preventivi/pratica/${praticaId}`);
        return response.data;
    },
    // Preventivi/proforma collegati a un servizio fiduciario.
    getByServizio: async (servizioId: number | string): Promise<ApiResponse<PreventivoDto[]>> => {
        const response = await api.get<ApiResponse<PreventivoDto[]>>(`/preventivi/servizio/${servizioId}`);
        return response.data;
    },
    // Preventivi/proforma di un cliente non ancora collegati a un mandato,
    // proposti in creazione e modifica del mandato.
    getPendingByClient: async (clientId: number | string): Promise<ApiResponse<PreventivoDto[]>> => {
        const response = await api.get<ApiResponse<PreventivoDto[]>>(`/preventivi/pending-by-client/${clientId}`);
        return response.data;
    },
    // Lista paginata lato server: linked=false preventivi standalone, linked=true
    // quelli associati a un mandato. La response include i conteggi di entrambi i tab.
    getPaged: async (params: {
        linked?: boolean;
        page?: number;
        size?: number;
        stato?: string;
        q?: string;
        // Sede selezionata (responsabile): la lista segue il selettore come la dashboard.
        sedeId?: number;
    }): Promise<ApiResponse<{
        content: PreventivoDto[];
        totalPages: number;
        totalElements: number;
        number: number;
        size: number;
        first: boolean;
        last: boolean;
        empty: boolean;
        countStandalone: number;
        countLinked: number;
    }>> => {
        const qs = new URLSearchParams();
        qs.set('linked', String(params.linked ?? false));
        qs.set('page', String(params.page ?? 0));
        qs.set('size', String(params.size ?? 25));
        if (params.stato && params.stato !== 'ALL') qs.set('stato', params.stato);
        if (params.q && params.q.trim()) qs.set('q', params.q.trim());
        if (typeof params.sedeId === 'number') qs.set('sedeId', String(params.sedeId));
        const response = await api.get(`/preventivi/paged?${qs.toString()}`);
        return response.data;
    },
    // Carica il PDF proforma generato nel browser; il BE lo salva su S3 e
    // marca il record come PROFORMA.
    savePdf: async (id: number | string, pdfBlob: Blob, filename: string): Promise<ApiResponse<PreventivoDto>> => {
        const fd = new FormData();
        fd.append('pdf', pdfBlob, filename);
        const response = await api.post<ApiResponse<PreventivoDto>>(`/preventivi/${id}/pdf`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
    // Collega un preventivo a un mandato (null per scollegare). Il BE verifica
    // che il cliente coincida.
    linkToPratica: async (id: number | string, praticaId: number | null): Promise<ApiResponse<PreventivoDto>> => {
        const response = await api.put<ApiResponse<PreventivoDto>>(`/preventivi/${id}/pratica`, { praticaId });
        return response.data;
    },
    // Collega/scollega un preventivo standalone a un servizio fiduciario (null scollega).
    linkToServizio: async (id: number | string, servizioId: number | null): Promise<ApiResponse<PreventivoDto>> => {
        const response = await api.put<ApiResponse<PreventivoDto>>(`/preventivi/${id}/servizio`, { servizioId });
        return response.data;
    },
    // Flag "fattura elettronica emessa" sulla proforma.
    setFatturaElettronica: async (id: number | string, emessa: boolean, numero?: string): Promise<ApiResponse<PreventivoDto>> => {
        const response = await api.put<ApiResponse<PreventivoDto>>(`/preventivi/${id}/fattura-elettronica`, {
            emessa, numero: numero || null,
        });
        return response.data;
    },
};

export const ScadenzaService = {
    getAll: async () => (await api.get('/scadenze')).data,
    create: async (data: any) => (await api.post('/scadenze', data)).data,
    complete: async (id: number) => (await api.put(`/scadenze/${id}/complete`)).data,
    delete: async (id: number) => (await api.delete(`/scadenze/${id}`)).data,
};

export const ActivityTypeService = {
    getAll: async () => (await api.get('/activities')).data,
    create: async (data: ActivityType) => (await api.post('/activities', data)).data,
    update: async (id: number, data: ActivityType) => (await api.put(`/activities/${id}`, data)).data,
    delete: async (id: number) => (await api.delete(`/activities/${id}`)).data,
};

export const IncaricoService = {
    getMyIncarichi: async () => (await api.get('/incarichi/me')).data,
    // Verifica preliminare della foto km prima di start/stop: 200 se l'EXIF è presente
    // e recente, 422 con messaggio se manca o è fuori tolleranza.
    validateKmPhoto: (incaricoId: number, foto: File, clientTimestamp?: string) => {
        const fd = new FormData();
        fd.append('foto', foto);
        if (clientTimestamp) fd.append('clientTimestamp', clientTimestamp);
        return api.post(`/incarichi/${incaricoId}/validate-km-photo`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    // Storico paginato con filtro date (default BE: ultimi 30 giorni, page 0, size 25).
    // Risposta: { success, data: { content, page, size, totalElements, totalPages, from, to } }
    getMyIncarichiStorico: async (params: {
        page?: number;
        size?: number;
        from?: string; // YYYY-MM-DD
        to?: string;   // YYYY-MM-DD
    } = {}) => {
        const qs = new URLSearchParams();
        if (params.page !== undefined) qs.set('page', String(params.page));
        if (params.size !== undefined) qs.set('size', String(params.size));
        if (params.from) qs.set('from', params.from);
        if (params.to) qs.set('to', params.to);
        const url = '/incarichi/me/storico' + (qs.toString() ? `?${qs.toString()}` : '');
        const response = await api.get(url);
        return response.data?.data ?? response.data;
    },
    getAll: async (): Promise<ApiResponse<T.Incarico[]>> => {
        const response = await api.get<ApiResponse<T.Incarico[]>>('/incarichi');
        return response.data;
    },
    getPraticheAutorizzate: async () => {
        const response = await api.get('/incarichi/pratiche-autorizzate');
        return response.data;
    },
    createIncaricoRapido: async (praticaId: number, note?: string, soggettoNome?: string) => {
        const params = new URLSearchParams({ praticaId: String(praticaId) });
        if (note) params.set('note', note);
        if (soggettoNome) params.set('soggettoNome', soggettoNome);
        const response = await api.post(`/incarichi/rapido?${params.toString()}`);
        return response.data;
    },
    // Apre un incarico urgente sulla stessa pratica dell'incarico bloccato
    // (report di oggi già inviato): auto-assegnato, parte IN_CORSO, notifica il titolare.
    createUrgenteFrom: async (fromIncaricoId: number) => {
        const response = await api.post(`/incarichi/${fromIncaricoId}/urgente`);
        return response.data;
    },

    // Incarico urgente senza mandato: il collaboratore lavora con un nome libero e il
    // titolare lo abbina poi al mandato da /approvazioni. Crea un incarico orfano
    // con finestra [adesso, oggi 23:59].
    createUrgenteSenzaMandato: async (nome: string) => {
        const response = await api.post('/incarichi/urgente-senza-mandato', { nome });
        return response.data;
    },

    // Abbina un incarico orfano a un mandato esistente (solo titolare/responsabile)
    // e aggiorna il soggettoNome. Con più soggetti va passato il nome scelto,
    // altrimenti il BE risponde 409 con `data.soggettiDisponibili`.
    abbina: async (incaricoId: number, praticaId: number, soggettoNome?: string) => {
        const payload: { praticaId: number; soggettoNome?: string } = { praticaId };
        if (soggettoNome) payload.soggettoNome = soggettoNome;
        const response = await api.post(`/incarichi/${incaricoId}/abbina`, payload);
        return response.data;
    },
    // In alternativa al mandato l'orfano diventa un turno già svolto di un servizio:
    // la proposta precompila data e orari (start + ore cliente del report).
    getPropostaTurno: async (incaricoId: number) => {
        const response = await api.get(`/incarichi/${incaricoId}/proposta-turno`);
        return response.data?.data;
    },
    // I media dell'incarico vengono eliminati: serve confermaEliminazioneMedia se ce ne sono.
    convertiInTurno: async (incaricoId: number, payload: {
        servizioId: number; dataTurno: string; oraInizio: string; oraFine: string;
        confermaEliminazioneMedia: boolean; tariffaOraria?: number;
    }) => {
        const response = await api.post(`/incarichi/${incaricoId}/converti-in-turno`, payload);
        return response.data;
    },
    // Incarico urgente dall'Agenda Operativa su una pratica scelta. A differenza di
    // /rapido (AUTOGENERATO, invisibile in /me) il collaboratore lo vede subito in "Oggi".
    createUrgenteDaPratica: async (praticaId: number, note?: string, soggettoNome?: string) => {
        const params = new URLSearchParams();
        params.set('praticaId', String(praticaId));
        if (note) params.set('note', note);
        if (soggettoNome) params.set('soggettoNome', soggettoNome);
        const response = await api.post(`/incarichi/urgente-da-pratica?${params.toString()}`);
        return response.data;
    },
    // Tutti gli upload vanno diretti su S3 dal browser e il BE riceve solo la chiave:
    // così il fileKey dell'originale è scritto in modo sincrono e non può andare perso.
    // `uploadToS3Multipart` usa un singolo PUT per file fino a 25 MB.
    uploadMedia: async (incaricoId: number, file: File, categoria?: string, incaricoStartDate?: string, onChunkUploaded?: (bytes: number) => void, bypassChecks?: boolean, applyDateWatermark?: boolean) => {
        // Nessun fallback al vecchio upload multipart: se S3 fallisce meglio mostrare
        // l'errore e far ritentare, piuttosto che rischiare di perdere l'originale.
        const s3Key = await IncaricoService.uploadToS3Multipart(file, onChunkUploaded);
        return await IncaricoService.processFromS3(incaricoId, s3Key, file.name, categoria, incaricoStartDate, bypassChecks, applyDateWatermark);
    },
    confirmDate: async (tempId: string, date: string) => {
        const response = await api.patch('/media/confirm-date', { tempId, date });
        return response.data;
    },
    // Media già persistiti (temp_id spesso null): si identificano per id.
    confirmDateById: async (mediaId: number, date: string) => {
        const response = await api.patch('/media/confirm-date', { mediaId, date });
        return response.data;
    },
    // Modifica le note di un incarico già assegnato (solo titolare/responsabile; 409 se terminale).
    updateNote: async (id: number, note: string) => {
        const response = await api.put(`/incarichi/${id}/note`, { note });
        return response.data;
    },
    // Modifica solo la data di fine, anche a incarico avviato. `dataFine` è un
    // LocalDateTime senza fuso. 400 se fine <= inizio o oltre il giorno dopo;
    // 409 se terminale o se la riduzione lascerebbe sessioni/foto/report fuori range.
    updateDataFine: async (id: number, dataFine: string) => {
        const response = await api.put(`/incarichi/${id}/data-fine`, { dataFine });
        return response.data;
    },
    // Sposta inizio e fine di un incarico non ancora avviato. 409 con `data.conflicts` se il
    // collaboratore è già impegnato (forceOverride li supera se tutti `overridable`).
    updateOrari: async (id: number, dataInizio: string, dataFine: string, forceOverride = false) => {
        const response = await api.put(`/incarichi/${id}/orari`, { dataInizio, dataFine, forceOverride });
        return response.data;
    },
    revoca: async (id: number) => {
        const response = await api.put(`/incarichi/${id}/revoca`);
        return response.data;
    },

    updateAutoAziendale: async (id: number, autoAziendale: boolean) => {
        const response = await api.put(`/incarichi/${id}/auto-aziendale`, { autoAziendale });
        return response.data;
    },

    // Il collaboratore accetta l'incarico: da ASSEGNATO ad ACCETTATO.
    accetta: async (id: number) => {
        const response = await api.put(`/incarichi/${id}/accetta`);
        return response.data;
    },

    // Rifiuto del collaboratore con motivo opzionale: da ASSEGNATO a RIFIUTATO.
    rifiuta: async (id: number, motivo?: string) => {
        const response = await api.put(`/incarichi/${id}/rifiuta`, motivo ? { motivo } : {});
        return response.data;
    },

    delete: async (id: number) => {
        const response = await api.delete(`/incarichi/${id}`);
        return response.data;
    },

    // download=true → stessa copia dell'anteprima, ma in attachment (scarica, non apre).
    // originale=true (solo con download): il file caricato, senza conversione né timbro.
    getMediaUrl: async (mediaId: number, download: boolean = false, originale: boolean = false): Promise<string> => {
        const params = { ...(download ? { download: true } : {}), ...(originale ? { originale: true } : {}) };
        const res = await api.get(`/media/${mediaId}/url`, Object.keys(params).length ? { params } : undefined);
        return res.data.data;
    },

    // Foto km dell'incarico da endpoint dedicato: inc.media non è affidabile
    // nei mandati storici già approvati.
    getKmPhotos: async (incaricoId: number): Promise<any[]> => {
        const res = await api.get(`/incarichi/${incaricoId}/km-photos`);
        const data = res.data?.data ?? res.data;
        return Array.isArray(data) ? data : [];
    },

    deleteMedia: async (mediaId: number): Promise<any> => {
        return (await api.delete(`/media/${mediaId}`)).data;
    },

    // "Tieni comunque" un media segnalato caricato da File Manager, senza approvazione.
    // Solo admin/responsabile.
    keepMedia: async (mediaId: number): Promise<any> => {
        return (await api.post(`/media/${mediaId}/keep`)).data;
    },

    // Rielabora il media applicando o rimuovendo il watermark (solo admin/responsabile).
    reprocessWatermark: async (mediaId: number, apply: boolean): Promise<any> => {
        return (await api.post(`/media/${mediaId}/reprocess-watermark`, null, { params: { apply } })).data;
    },

    // Polling leggero dopo reprocessWatermark: `status` torna COMPLETED quando
    // la nuova versione compressa è pronta.
    getMediaProcessingStatus: async (mediaId: number): Promise<{ status: string | null; watermarkApplied: boolean | null }> => {
        const response = await api.get(`/media/${mediaId}/processing-status`);
        return response.data?.data;
    },

    deleteMediaByTempId: async (tempId: string): Promise<any> => {
        return (await api.delete(`/media/by-temp-id/${tempId}`)).data;
    },

    // Rigenera la versione compressa senza timbro data partendo dall'originale.
    // Solo admin/responsabile.
    reprocessMediaNoWatermark: async (ids: number[]): Promise<any> => {
        return (await api.post('/media/reprocess', { ids })).data;
    },

    // Aggiunge (apply=true) o toglie il timbro data in blocco; i media non idonei
    // tornano in skippedIds.
    reprocessMediaWatermark: async (ids: number[], apply: boolean): Promise<any> => {
        return (await api.post('/media/reprocess', { ids, apply })).data;
    },

    // `base` = prefisso degli endpoint: '/media' per le prove, '/personal-drive/upload' per il drive.
    getPresignedUploadUrl: async (filename: string, contentType: string, base: string = '/media'): Promise<{ presignedUrl: string; s3Key: string }> => {
        const res = await api.get(`${base}/presigned-url`, { params: { filename, contentType } });
        return res.data?.data ?? res.data;
    },

    // Upload multipart browser -> S3 in chunk paralleli
    startMultipartUpload: async (filename: string, contentType: string, base: string = '/media'): Promise<{ uploadId: string; s3Key: string }> => {
        const res = await api.get(`${base}/multipart/start`, { params: { filename, contentType } });
        return res.data?.data ?? res.data;
    },

    getPresignedPartUrl: async (s3Key: string, uploadId: string, partNumber: number, base: string = '/media'): Promise<string> => {
        const res = await api.get(`${base}/multipart/part-url`, { params: { s3Key, uploadId, partNumber } });
        return (res.data?.data ?? res.data).presignedUrl as string;
    },

    completeMultipartUpload: async (s3Key: string, uploadId: string, parts: { partNumber: number; eTag: string }[], base: string = '/media'): Promise<void> => {
        await api.post(`${base}/multipart/complete`, { s3Key, uploadId, parts });
    },

    /**
     * Carica un file su S3 in chunk paralleli (multipart presigned) e restituisce la s3Key.
     * Chunk da 25MB con 4 worker; i file fino a 25MB usano un singolo PUT.
     * onChunkUploaded riceve i byte di ogni chunk completato (per la velocità in UI).
     */
    uploadToS3Multipart: async (file: File, onChunkUploaded?: (bytes: number) => void, base: string = '/media'): Promise<string> => {
        const CHUNK = 25 * 1024 * 1024;
        if (file.size <= CHUNK) {
            const { presignedUrl, s3Key } = await IncaricoService.getPresignedUploadUrl(
                file.name, file.type || 'application/octet-stream', base);
            const res = await fetch(presignedUrl, {
                method: 'PUT', body: file,
                headers: { 'Content-Type': file.type || 'application/octet-stream' },
            });
            if (!res.ok) throw new Error(`Upload fallito: ${res.status}`);
            onChunkUploaded?.(file.size);
            return s3Key;
        }
        const { uploadId, s3Key } = await IncaricoService.startMultipartUpload(
            file.name, file.type || 'application/octet-stream', base);
        const totalParts = Math.ceil(file.size / CHUNK);
        const parts: { partNumber: number; eTag: string }[] = new Array(totalParts);
        let next = 0;
        const worker = async () => {
            while (true) {
                const i = next++;
                if (i >= totalParts) return;
                const partNumber = i + 1;
                const blob = file.slice(i * CHUNK, Math.min((i + 1) * CHUNK, file.size));
                const url = await IncaricoService.getPresignedPartUrl(s3Key, uploadId, partNumber, base);
                const res = await fetch(url, { method: 'PUT', body: blob });
                if (!res.ok) throw new Error(`Part ${partNumber} failed: ${res.status}`);
                const eTag = res.headers.get('ETag') || res.headers.get('etag') || '';
                parts[i] = { partNumber, eTag };
                onChunkUploaded?.(blob.size);
            }
        };
        await Promise.all(Array.from({ length: Math.min(4, totalParts) }, worker));
        await IncaricoService.completeMultipartUpload(s3Key, uploadId, parts, base);
        return s3Key;
    },

    /**
     * Passa al backend una s3Key già caricata: il file viene scaricato lato server
     * per EXIF, hash e conversione.
     */
    processFromS3: async (incaricoId: number, s3Key: string, filename: string, categoria?: string, incaricoStartDate?: string, bypassChecks?: boolean, applyDateWatermark?: boolean): Promise<any> => {
        const params: Record<string, string> = { incaricoId: String(incaricoId), s3Key, filename };
        if (categoria) params.categoria = categoria;
        if (incaricoStartDate) params.incaricoStartDate = incaricoStartDate;
        if (bypassChecks) params.bypassChecks = 'true';
        // Il default lato BE è true: lo inviamo solo quando l'utente lo disattiva.
        if (applyDateWatermark === false) params.applyDateWatermark = 'false';
        const response = await api.post('/media/process-from-s3', null, { params });
        return response.data?.data ?? response.data;
    },

    updateMediaDate: async (mediaId: number, dataAcquisizione: string): Promise<any> => {
        return (await api.patch(`/media/${mediaId}/date`, { dataAcquisizione })).data;
    },

    addNote: async (incaricoId: number, text: string, lat?: number, lon?: number, dateStr?: string) => {
        const params: any = {};
        if(lat) params.lat = lat;
        if(lon) params.lon = lon;
        if(dateStr) params.date = dateStr;

        const response = await api.post(`/incarichi/${incaricoId}/note`, { text }, { params });
        return response.data;
    },

    // Lista compatta delle note operative, per aggiornarle senza ricaricare l'incarico.
    getNote: async (incaricoId: number): Promise<ApiResponse<T.IncaricoNote[]>> => {
        const response = await api.get(`/incarichi/${incaricoId}/note`);
        return response.data;
    },

    create: async (data: {
        praticaId: number,
        collaboratoreId: number,
        soggettoNome: string,
        dataInizio: string,
        dataFine: string,
        luogo: string,
        note: string,
        // Scadenza opzionale per l'accettazione: oltre questa il backend auto-rifiuta l'incarico.
        dataScadenzaAccettazione?: string | null,
        // Orari da tasti rapidi (fine 23:59): niente alert "orari difformi",
        // altrimenti ogni chiusura anticipata risulterebbe fuori soglia.
        orariPreset?: boolean,
        // Assegna comunque se gli unici conflitti sono superabili (stop già cliccato);
        // l'operazione viene registrata in audit. Valido solo per admin/responsabile.
        forceOverride?: boolean,
        // Incarico unico a cavallo della mezzanotte invece di spezzarlo per giorno.
        // Ammesso solo se la fine cade il giorno dopo (altrimenti 400).
        continuativo?: boolean,
    }) => {
        const response = await api.post('/incarichi', data);
        return response.data;
    },

    // Il BE esclude di default gli incarichi AUTOGENERATO; le viste media li
    // richiedono per mostrare i file caricati.
    getByPratica: async (
        praticaId: number,
        opts?: { includeAutogenerati?: boolean }
    ): Promise<ApiResponse<T.Incarico[]>> => {
        const params = opts?.includeAutogenerati ? { includeAutogenerati: true } : undefined;
        const response = await api.get<ApiResponse<T.Incarico[]>>(`/incarichi/pratica/${praticaId}`, { params });
        return response.data;
    },

    start: async (id: number, lat: number, lon: number) =>
        (await api.post(`/incarichi/${id}/start`, null, { params: { lat, lon } })).data,

    stop: async (sessionId: number, lat: number, lon: number) =>
        (await api.post(`/incarichi/session/${sessionId}/stop`, null, { params: { lat, lon } })).data,

    uploadMediaChunked: async (
        incaricoId: number,
        file: File,
        lat: number,
        lon: number,
        uid: string,
        onProgress: (percent: number) => void,
        dataAcquisizione?: string,
        categoria?: string,
        incaricoStartDate?: string,
        bypassChecks?: boolean,
        applyDateWatermark?: boolean
    ) => {
        // Chunk da 10MB per ridurre i round-trip; 5 worker per restare sotto il limite
        // di 6 connessioni HTTP/1.1 per host.
        const CHUNK_SIZE = 10 * 1024 * 1024;
        const PARALLEL = 5;
        const MAX_RETRIES = 3;
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

        let completed = 0;
        // Teniamo la risposta del chunk che fa scattare l'assembly: contiene
        // { tempId, status, date } come l'endpoint a POST singola.
        let assemblyResponse: any = null;

        const sendChunk = async (chunkIndex: number, attempt = 0): Promise<void> => {
            const start = chunkIndex * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, file.size);
            const chunk = file.slice(start, end);

            const formData = new FormData();
            formData.append('incaricoId', incaricoId.toString());
            formData.append('file', chunk);
            formData.append('chunkIndex', chunkIndex.toString());
            formData.append('totalChunks', totalChunks.toString());
            formData.append('fileName', file.name);
            formData.append('uid', uid);
            if (lat) formData.append('lat', lat.toString());
            if (lon) formData.append('lon', lon.toString());
            if (dataAcquisizione) formData.append('dataAcquisizione', dataAcquisizione);
            // I metadati vanno su ogni chunk: non si sa quale arriverà per ultimo
            // e farà partire l'assembly.
            if (categoria) formData.append('categoria', categoria);
            if (incaricoStartDate) formData.append('incaricoStartDate', incaricoStartDate);
            if (bypassChecks) formData.append('bypassChecks', 'true');
            // Il default lato BE è true: lo inviamo solo quando l'utente lo disattiva.
            if (applyDateWatermark === false) formData.append('applyDateWatermark', 'false');

            try {
                // Timeout ampio: l'ultimo chunk attende assembly e upload su S3 e può metterci minuti.
                const res = await api.post('/media/chunk', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    timeout: 10 * 60 * 1000,
                });
                completed++;
                onProgress(Math.round((completed / totalChunks) * 100));
                // Solo la chiamata che ha eseguito l'assembly ha data non null.
                const payload = res.data?.data;
                if (payload && (payload.tempId || payload.status)) {
                    assemblyResponse = payload;
                }
            } catch (e) {
                if (attempt < MAX_RETRIES) {
                    // Backoff esponenziale: 1s, 2s, 4s
                    await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
                    return sendChunk(chunkIndex, attempt + 1);
                }
                throw e;
            }
        };

        // Worker che pescano da un contatore condiviso; l'assembly parte lato BE
        // solo quando tutti i chunk sono su disco.
        let nextIdx = 0;
        const worker = async () => {
            while (true) {
                const idx = nextIdx++;
                if (idx >= totalChunks) return;
                await sendChunk(idx);
            }
        };
        await Promise.all(Array.from({ length: Math.min(PARALLEL, totalChunks) }, worker));

        // Stessa forma dell'endpoint a POST singola, così i chiamanti non distinguono i due casi.
        return assemblyResponse ?? { tempId: uid, status: 'ready' };
    },

    deleteNote: async (noteId: number) => {
        const response = await api.delete(`/incarichi/notes/${noteId}`);
        return response.data;
    },

    getMediaAnalysisStatus: async (mediaId: number): Promise<import('../types').FileAnalysisResultDto> => {
        const response = await api.get(`/media/${mediaId}/analysis-status`);
        return response.data?.data;
    },

    // incaricoId nullable: per i media orfani gli item possono venire da incarichi
    // diversi e il BE usa l'incarico di ciascun media.
    submitPendingApproval: async (incaricoId: number | null, items: { mediaId: number; reason: string }[]): Promise<any> => {
        const response = await api.post('/media/pending-approval', { items, incaricoId });
        return response.data;
    },

    getPendingApprovals: async (sedeId?: number): Promise<PendingApprovalItem[]> => {
        const params = sedeId ? { sedeId } : {};
        const response = await api.get('/media/pending-approval', { params });
        return response.data?.data ?? response.data ?? [];
    },

    // Media rimasti in sospeso (DUPLICATE/OUTSIDE_RANGE senza invio o NO_EXIF senza data),
    // per riaprire la decisione da "Media da rivedere". Il BE filtra per ruolo.
    getOrfani: async (sedeId?: number): Promise<import('../types').FileAnalysisResultDto[]> => {
        const params = typeof sedeId === 'number' ? { sedeId } : {};
        const response = await api.get('/media/orfani', { params });
        return response.data?.data ?? response.data ?? [];
    },

    approvePendingApproval: async (id: number): Promise<any> => {
        const response = await api.post(`/media/pending-approval/${id}/approve`);
        return response.data;
    },

    rejectPendingApproval: async (id: number): Promise<any> => {
        const response = await api.post(`/media/pending-approval/${id}/reject`);
        return response.data;
    },
};

export const AmministrazioneService = {
    getStats: async (sedeId: number | 'ALL', from?: string, to?: string) => {
        // from/to in formato YYYY-MM-DD; se omessi il BE usa il mese corrente.
        const params: string[] = [];
        if (sedeId !== 'ALL') params.push(`sedeId=${sedeId}`);
        if (from) params.push(`from=${from}`);
        if (to) params.push(`to=${to}`);
        const qs = params.length ? `?${params.join('&')}` : '';
        const response = await api.get<ApiResponse<FinancialStats>>(`/amministrazione/stats${qs}`);
        return response.data;
    },

    getMovimenti: async (sedeId: number | 'ALL') => {
        const param = sedeId === 'ALL' ? '' : `?sedeId=${sedeId}`;
        const response = await api.get<ApiResponse<MovimentoContabile[]>>(`/amministrazione/movimenti${param}`);
        return response.data;
    },

    // Registro movimenti con filtro periodo e pre-filtro per contesto (contabilità/mandati/servizi).
    getRegistroMovimenti: async (filters: T.RegistroMovimentiFilters = {}): Promise<ApiResponse<MovimentoContabile[]>> => {
        const qs = new URLSearchParams();
        if (filters.sedeId !== undefined && filters.sedeId !== 'ALL' && filters.sedeId !== null) {
            qs.set('sedeId', String(filters.sedeId));
        }
        if (filters.from) qs.set('from', filters.from);
        if (filters.to) qs.set('to', filters.to);
        if (filters.preFilter && filters.preFilter !== 'ALL') qs.set('preFilter', filters.preFilter);
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        const response = await api.get<ApiResponse<MovimentoContabile[]>>(`/amministrazione/registro-movimenti${suffix}`);
        return response.data;
    },

    // Modifica una riga orfana del registro; 403 se la sorgente è ancora attiva.
    editMovimento: async (sourceKey: string, patch: T.MovimentoEditRequest): Promise<ApiResponse<boolean>> => {
        const enc = encodeURIComponent(sourceKey);
        const response = await api.patch<ApiResponse<boolean>>(`/amministrazione/registro-movimenti/${enc}`, patch);
        return response.data;
    },

    // Eliminazione logica (importo a 0).
    deleteMovimento: async (sourceKey: string): Promise<ApiResponse<boolean>> => {
        const enc = encodeURIComponent(sourceKey);
        const response = await api.delete<ApiResponse<boolean>>(`/amministrazione/registro-movimenti/${enc}`);
        return response.data;
    },

    // PDF della vista corrente del registro.
    exportRegistroMovimentiPdf: async (filters: T.RegistroMovimentiFilters = {}): Promise<Blob> => {
        const qs = new URLSearchParams();
        if (filters.sedeId !== undefined && filters.sedeId !== 'ALL' && filters.sedeId !== null) {
            qs.set('sedeId', String(filters.sedeId));
        }
        if (filters.from) qs.set('from', filters.from);
        if (filters.to) qs.set('to', filters.to);
        if (filters.preFilter && filters.preFilter !== 'ALL') qs.set('preFilter', filters.preFilter);
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        const response = await api.get(`/amministrazione/registro-movimenti/pdf${suffix}`, { responseType: 'blob' });
        return response.data as Blob;
    },
    getReportMensile: async (anno: number, mese: number, sedeId?: number | 'ALL' | null): Promise<ApiResponse<any[]>> => {
        const sedeParam = (sedeId && sedeId !== 'ALL') ? `&sedeId=${sedeId}` : '';
        const response = await api.get(`/amministrazione/report-mensile?anno=${anno}&mese=${mese}${sedeParam}`);
        return response.data;
    },

    // Costi collaboratori da inizio anno per la dashboard, filtrati per sede del mandato
    // (coerente con fatturato/incassato) e non del collaboratore.
    getCostiCollaboratoriYTD: async (anno: number, sedeId?: number | string | null): Promise<ApiResponse<number>> => {
        const sedeParam = (sedeId && sedeId !== 'ALL') ? `&sedeId=${sedeId}` : '';
        const response = await api.get(`/amministrazione/costi-collaboratori-ytd?anno=${anno}${sedeParam}`);
        return response.data;
    },

    // Costi collaboratori sul range del selettore di periodo della Dashboard.
    // from/to in formato ISO YYYY-MM-DD; sedeId opzionale.
    getCostiCollaboratoriRange: async (from: string, to: string, sedeId?: number | string | null): Promise<ApiResponse<number>> => {
        const sedeParam = (sedeId && sedeId !== 'ALL') ? `&sedeId=${sedeId}` : '';
        const response = await api.get(`/amministrazione/costi-collaboratori-range?from=${from}&to=${to}${sedeParam}`);
        return response.data;
    },

    // Spese fisse pesate sul range, ricostruite dall'audit per tenere conto
    // delle variazioni di importo nel tempo.
    getSpeseFisseRange: async (from: string, to: string, sedeId?: number | string | null): Promise<ApiResponse<number>> => {
        const sedeParam = (sedeId && sedeId !== 'ALL') ? `&sedeId=${sedeId}` : '';
        const response = await api.get(`/amministrazione/spese-fisse-range?from=${from}&to=${to}${sedeParam}`);
        return response.data;
    },

    getSpeseFisse: async (sedeId: string | number) => {
        const param = sedeId === 'ALL' ? '' : `?sedeId=${sedeId}`;
        const response = await api.get(`/amministrazione/spese-fisse${param}`);
        return response.data;
    },

    // Storico variazioni delle spese fisse.
    getStoricoSpeseFisse: async (params: { from?: string; to?: string; sedeId?: number | 'ALL' } = {}) => {
        const qs = new URLSearchParams();
        if (params.from) qs.set('from', params.from);
        if (params.to) qs.set('to', params.to);
        if (params.sedeId !== undefined && params.sedeId !== 'ALL') qs.set('sedeId', String(params.sedeId));
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        const response = await api.get(`/amministrazione/spese-fisse/storico${suffix}`);
        return response.data;
    },

    addSpesaFissa: async (data: { sedeId: string | number | null, voce: string, importo: number }) => {
        const response = await api.post(`/amministrazione/spese-fisse`, data);
        return response.data;
    },
    deleteSpesaFissa: async (id: number) => {
        const response = await api.delete(`/amministrazione/spese-fisse/${id}`);
        return response.data;
    },

    // Entrate e uscite una tantum.
    getMovimentiManuali: async (params: { sedeId?: number | string | null; from?: string; to?: string } = {}) => {
        const qs = new URLSearchParams();
        if (params.sedeId !== undefined && params.sedeId !== null && params.sedeId !== 'ALL') qs.set('sedeId', String(params.sedeId));
        if (params.from) qs.set('from', params.from);
        if (params.to) qs.set('to', params.to);
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        const response = await api.get(`/amministrazione/movimenti-manuali${suffix}`);
        return response.data;
    },
    addMovimentoManuale: async (data: T.MovimentoManualePayload) => {
        const response = await api.post(`/amministrazione/movimenti-manuali`, data);
        return response.data;
    },
    updateMovimentoManuale: async (id: number, data: T.MovimentoManualePayload) => {
        const response = await api.put(`/amministrazione/movimenti-manuali/${id}`, data);
        return response.data;
    },
    deleteMovimentoManuale: async (id: number) => {
        const response = await api.delete(`/amministrazione/movimenti-manuali/${id}`);
        return response.data;
    },

    getPraticheNonSaldate: async (sedeId: number | 'ALL') => {
        const param = sedeId === 'ALL' ? '' : `?sedeId=${sedeId}`;
        const response = await api.get<ApiResponse<T.PraticaNonSaldata[]>>(`/amministrazione/pratiche-non-saldate${param}`);
        return response.data;
    },

    // Mandati archiviati o eliminati con residuo non riscosso. `dal`/`al` (yyyy-MM-dd,
    // inclusi) filtrano sulla data di uscita, non su quella di creazione.
    getImportiNonRiscossi: async (params: { sedeId: number | 'ALL'; dal?: string; al?: string }) => {
        const qs = new URLSearchParams();
        if (params.sedeId !== 'ALL') qs.set('sedeId', String(params.sedeId));
        if (params.dal) qs.set('dal', params.dal);
        if (params.al) qs.set('al', params.al);
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        const response = await api.get<ApiResponse<T.PraticaNonSaldata[]>>(`/amministrazione/importi-non-riscossi${suffix}`);
        return response.data;
    },

    getPraticheNonFatturate: async (sedeId: number | 'ALL') => {
        const param = sedeId === 'ALL' ? '' : `?sedeId=${sedeId}`;
        const response = await api.get<ApiResponse<T.PraticaNonFatturata[]>>(`/amministrazione/pratiche-non-fatturate${param}`);
        return response.data;
    },

    // Equivalenti per i servizi fiduciari.
    getServiziNonSaldati: async (sedeId: number | 'ALL') => {
        const param = sedeId === 'ALL' ? '' : `?sedeId=${sedeId}`;
        const response = await api.get<ApiResponse<T.ServizioNonSaldato[]>>(`/amministrazione/servizi-non-saldati${param}`);
        return response.data;
    },
    getServiziNonFatturati: async (sedeId: number | 'ALL') => {
        const param = sedeId === 'ALL' ? '' : `?sedeId=${sedeId}`;
        const response = await api.get<ApiResponse<T.ServizioNonFatturato[]>>(`/amministrazione/servizi-non-fatturati${param}`);
        return response.data;
    },

    // Categorie di log esportabili (definite lato BE).
    listLogExportCategories: async (): Promise<string[]> => {
        const response = await api.get<ApiResponse<{ categories: string[] }>>(`/admin/log-export/categories`);
        return response.data?.data?.categories ?? [];
    },

    // Scarica lo ZIP dei log filtrati per periodo e categorie (download automatico).
    exportLogsZip: async (params: { from: string; to: string; categories: string[] }): Promise<void> => {
        const response = await api.post(`/admin/log-export`, params, { responseType: 'blob' });
        const blob = new Blob([response.data], { type: 'application/zip' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `logs_export_${params.from}_${params.to}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 10000); // Safari annulla il download se si revoca subito
    },

};

export const SpeseService = {
    // Invio di una spesa/ore da parte del collaboratore. I chiamanti mostrano il messaggio
    // del BE (incarico non suo o non più attivo): niente toast generico sul 403.
    create: async (formData: FormData): Promise<ApiResponse<any>> => {
        const response = await api.post('/spese', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            skipGlobalErrorToast: true,
        });
        return response.data;
    },

    update: async (id: number, formData: FormData): Promise<ApiResponse<any>> => {
        const response = await api.put(`/spese/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    },

    // Spese IN_ATTESA (admin/responsabile)
    getPending: async (sedeId?: number | 'ALL'): Promise<ApiResponse<any[]>> => {
        const param = sedeId && sedeId !== 'ALL' ? `?sedeId=${sedeId}` : '';
        const response = await api.get(`/spese/pending${param}`);
        return response.data;
    },

    // Approva o rifiuta una spesa
    review: async (id: number, data: { stato: 'APPROVATO' | 'RIFIUTATO' | 'MODIFICATO', importoApprovato?: number, noteAdmin?: string, anticipatoAziendaOverride?: boolean }): Promise<ApiResponse<any>> => {
        const response = await api.put(`/spese/${id}/review`, data);
        return response.data;
    },

    // Spese di un collaboratore per mese/anno
    getByCollaboratore: async (userId: number, mese: number, anno: number): Promise<ApiResponse<any[]>> => {
        const response = await api.get(`/spese/collaboratore/${userId}`, {
            params: { mese, anno }
        });
        return response.data;
    },

    // Riepilogo globale di tutti i collaboratori (admin)
    getAllByMonth: async (mese: number, anno: number): Promise<ApiResponse<any[]>> => {
        const response = await api.get('/spese/report-mensile', {
            params: { mese, anno }
        });
        return response.data;
    },

    delete: async (id: number, relazioneId?: number | null): Promise<ApiResponse<boolean>> => {
        const params = relazioneId != null ? `?relazioneId=${relazioneId}` : '';
        const response = await api.delete(`/spese/${id}${params}`);
        return response.data;
    }
};

// Incarichi e turni già svolti, inseriti con report già approvato (multipart come il report).
export const LavoroAPosterioriService = {
    creaIncarico: async (formData: FormData): Promise<any> => {
        const response = await api.post(`/incarichi/a-posteriori`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
    creaTurno: async (servizioId: number, formData: FormData): Promise<any> => {
        const response = await api.post(`/servizi/${servizioId}/turni/a-posteriori`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
};

export const RelazioneGiornalieraService = {
    create: async (formData: FormData): Promise<any> => {
        // Header esplicito per l'invio multipart (JSON + scontrini)
        const response = await api.post(`/relazioni-giornaliere`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    },

    getAllApprovedByMonth: async (monthStr: string): Promise<any> => {
        const response = await api.get(`/relazioni-giornaliere/mensile?month=${monthStr}`);
        return response.data;
    },

    getByPratica: async (praticaId: number): Promise<any> => {
        const response = await api.get(`/relazioni-giornaliere/pratica/${praticaId}`);
        return response.data;
    },
    getByTurno: async (turnoId: number): Promise<any> => {
        const response = await api.get(`/relazioni-giornaliere/turno/${turnoId}`);
        return response.data;
    },
    // Il range dataDal/dataAl ha priorità su month (filtro settimanale).
    getByServizio: async (
        servizioId: number,
        filter?: string | { dataDal?: string; dataAl?: string },
    ): Promise<any> => {
        let qs = '';
        if (typeof filter === 'string' && filter) {
            qs = `?month=${filter}`;
        } else if (filter && typeof filter === 'object' && filter.dataDal && filter.dataAl) {
            qs = `?dataDal=${filter.dataDal}&dataAl=${filter.dataAl}`;
        }
        const response = await api.get(`/relazioni-giornaliere/servizio/${servizioId}${qs}`);
        return response.data;
    },
    getById: async (id: number): Promise<any> => {
        const response = await api.get(`/relazioni-giornaliere/${id}`);
        return response.data?.data ?? response.data;
    },
    updateRevisione: async (id: number, payload: any): Promise<any> => {
        const response = await api.put(`/relazioni-giornaliere/${id}/revisione`, payload);
        return response.data;
    },
    // Approva in blocco i report con i valori inviati dal collaboratore.
    // Risposta { approvati, saltati }: i saltati non erano DA_REVISIONARE o non esistono.
    bulkApprove: async (ids: number[]): Promise<{ approvati: number[]; saltati: number[] }> => {
        const response = await api.post('/relazioni-giornaliere/bulk-approve', { ids });
        return response.data?.data ?? { approvati: [], saltati: [] };
    },
    annullaApprovazione: async (id: number): Promise<any> => {
        const response = await api.put(`/relazioni-giornaliere/${id}/annulla-approvazione`);
        return response.data;
    },
    // Scelta giornaliera tra auto propria e aziendale, per incarichi su più giorni.
    updateAutoAziendale: async (id: number, autoAziendale: boolean): Promise<any> => {
        const response = await api.put(`/relazioni-giornaliere/${id}/auto-aziendale`, { autoAziendale });
        return response.data;
    },
    getMie: async (): Promise<any[]> => {
        const res = await api.get('/relazioni-giornaliere/mie');
        return res.data?.data ?? res.data ?? [];
    },
    // Relazioni DA_MODIFICARE del collaboratore loggato (badge e banner).
    getMieDaModificare: async (): Promise<any[]> => {
        const res = await api.get('/relazioni-giornaliere/mie/da-modificare');
        return res.data?.data ?? res.data ?? [];
    },
    modifica: async (id: number, payload: {
        testoRelazione?: string;
        orarioInizioIndagine?: string;
        orarioFineIndagine?: string;
        oreExtra?: number;
        kmPercorsi?: number;
        autoAziendale?: boolean;
    }): Promise<any> => {
        const res = await api.put(`/relazioni-giornaliere/${id}/modifica`, payload);
        return res.data?.data ?? res.data;
    },
    // Rimanda il report al collaboratore in DA_MODIFICARE con il motivo.
    richiediModifica: async (id: number, motivo: string): Promise<any> => {
        const res = await api.put(`/relazioni-giornaliere/${id}/richiedi-modifica`, { motivo });
        return res.data?.data ?? res.data;
    },
    getPending: async (sedeId?: number | 'ALL'): Promise<any> => {
        const param = sedeId && sedeId !== 'ALL' ? `?sedeId=${sedeId}` : '';
        const response = await api.get(`/relazioni-giornaliere/pending${param}`);
        return response.data;
    },
};

// Conteggi pending per categoria (media / lavori / spese) e totale,
// per il widget della dashboard e il badge della sidebar.
export const ApprovazioniService = {
    getSummary: async (sedeId?: number | 'ALL'): Promise<{
        media: number; lavori: number; spese: number; daAbbinare: number; totale: number;
    }> => {
        const param = sedeId && sedeId !== 'ALL' ? `?sedeId=${sedeId}` : '';
        const res = await api.get(`/approvazioni/summary${param}`);
        const data = res.data?.data ?? res.data;
        return {
            media: Number(data?.media) || 0,
            lavori: Number(data?.lavori) || 0,
            spese: Number(data?.spese) || 0,
            daAbbinare: Number(data?.daAbbinare) || 0,
            totale: Number(data?.totale) || 0,
        };
    },
    // Incarichi orfani con relazione consegnata (tab "Da Abbinare").
    getIncarichiDaAbbinare: async (sedeId?: number | 'ALL') => {
        const param = sedeId && sedeId !== 'ALL' ? `?sedeId=${sedeId}` : '';
        const res = await api.get(`/approvazioni/incarichi-da-abbinare${param}`);
        return res.data?.data ?? res.data ?? [];
    },
    // Rifiuti recenti di incarichi e turni per il banner in dashboard
    // (default 7 giorni, il BE limita a 1..90).
    getRifiutiRecenti: async (
        sedeId?: number | 'ALL',
        giorni: number = 7,
    ): Promise<RifiutoRecenteDto[]> => {
        const params = new URLSearchParams();
        if (sedeId && sedeId !== 'ALL') params.set('sedeId', String(sedeId));
        params.set('giorni', String(giorni));
        const res = await api.get(`/approvazioni/rifiuti-recenti?${params.toString()}`);
        return (res.data?.data ?? res.data ?? []) as RifiutoRecenteDto[];
    },
};

// DTO dei rifiuti recenti.
export interface RifiutoRecenteDto {
    tipo: 'INCARICO' | 'TURNO';
    entityId: number;
    riferimento: string;
    mandatoId: number | null;
    servizioId: number | null;
    collaboratore: string;
    motivo: string | null;
    dataRifiuto: string;
    dataPianificata: string | null;
    sedeId: number | null;
}

// Riga del widget "Escalation aperte" in dashboard.
export interface EscalationAlertDto {
    notificaId: number;
    entity: 'incarico' | 'turno';
    entityId: number;
    tipo: string;            // PRE_START_60MIN / MISS_START_15 / MISS_STOP_60 / MISS_REPORT_12H / STOP_MAI_CHIUSO
    sentAt: string;
    riferimento: string;
    collaboratoreId: number | null;
    collaboratoreNome: string | null;
    collaboratoreTel: string | null;
    sedeId: number | null;
    // INFO è un semplice avviso (es. servizio fra un'ora) e va mostrato in stile neutro;
    // MEDIUM/HIGH/CRITICAL sono anomalie.
    severita: 'INFO' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
    // RISOLTO: il collaboratore ha poi eseguito l'azione mancante. APERTO: ancora fuori norma.
    statoAttuale: 'APERTO' | 'RISOLTO';
    risoltoAt: string | null;
    // Quando il collaboratore ha letto l'alert dalla push (null = non letto).
    ackAt: string | null;
}

// sedeId filtra per l'admin (undefined = tutte); per il responsabile il BE
// usa sempre la sua sede.
export const EscalationAlertService = {
    list: async (giorni: number = 7, sedeId?: number | null): Promise<EscalationAlertDto[]> => {
        const sedeParam = typeof sedeId === 'number' ? `&sedeId=${sedeId}` : '';
        const res = await api.get(`/escalation-aperte?giorni=${giorni}${sedeParam}`);
        return (res.data?.data ?? res.data ?? []) as EscalationAlertDto[];
    },
};

export const ArchivioService = {
    // `originals`: true per gli originali, false per la versione con watermark.
    // Se omesso il BE usa true.
    planZip: async (mediaIds: number[], fileIds: number[], label: string, originals?: boolean) => {
        const res = await api.post('/archivio/zip/plan', { mediaIds, fileIds, label, originals });
        return res.data?.data as { totalFiles: number; totalParts: number; fullToken?: string | null; parts: { token: string; fileCount: number; partNumber: number }[] };
    },
    getDownloadUrl: (token: string) => `${api.defaults.baseURL}/archivio/zip/download/${token}`,
};

export const AiService = {
    enhanceText: async (originalText: string, customPrompt?: string): Promise<any> => {
        const response = await api.post('/ai/enhance-text', { originalText, customPrompt });
        return response.data;
    }
};

// Endpoint distruttivo per i dati di test, disabilitato in produzione
// (`app.test-reset.enabled=false`). Chiedere sempre doppia conferma prima di reset().
export const TestDataService = {
    getStatus: async (): Promise<{ enabled: boolean }> => {
        try {
            const res = await api.get('/admin/test-data/status');
            const data = res.data?.data ?? res.data;
            return { enabled: !!data?.enabled };
        } catch {
            return { enabled: false };
        }
    },
    reset: async (): Promise<Record<string, number>> => {
        const res = await api.post('/admin/test-data/reset');
        return (res.data?.data ?? {}) as Record<string, number>;
    },
};